import { z } from "zod"

import type { Workspace } from "@productlane/zero"
import { Form, useZodForm } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { MetaInfo } from "@/components/meta-info"
import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import {
	SettingsCardInsetContext,
	SettingsContainer,
} from "@/screens/settings/settings-switches/settings-container"
import { WorkspaceLogoField } from "@/screens/settings/workspace-logo-field"
import { PortalPreview } from "../portal-preview"
import { SettingsHeader } from "../settings-header"
import {
	DarkModeField,
	EmbedImageField,
	ThemeColorFields,
	useThemeColorAutosave,
} from "./theme-fields"

const ThemeFormSchema = z.object({
	darkMode: z.boolean().nullable(),
	accentColor: z.string(),
	darkModeAccentColor: z.string(),
	buttonTextColor: z.string(),
	darkModeButtonTextColor: z.string(),
})

export function ThemeSettingsPage() {
	const workspace = useZeroWorkspace()

	const hasAnyPortalEnabled =
		workspace?.changelogPublic === true ||
		workspace?.helpCenterPublic === true ||
		workspace?.domainPublic === true ||
		workspace?.customerPortalSettings?.isEnabled === true

	const previewSettingsHash = JSON.stringify({
		accentColor: workspace?.accentColor,
		darkModeAccentColor: workspace?.darkModeAccentColor,
		buttonTextColor: workspace?.buttonTextColor,
		darkModeButtonTextColor: workspace?.darkModeButtonTextColor,
		darkMode: workspace?.darkMode,
		logoUrl: workspace?.logoUrl,
		portalEmbedImageUrl: workspace?.portalEmbedImageUrl,
	})

	return (
		<>
			<MetaInfo title="Theme Settings" />

			<SettingsHeader
				title="Theme"
				subtitle="Customize the theme of your widget and portal."
			/>
			<SettingsCardInsetContext.Provider value={false}>
				<div className="flex flex-col gap-8 lg:flex-row lg:justify-between">
					<div className="min-w-0 flex-1 lg:max-w-2xl">
						{workspace ? <ThemeForm data={{ workspace: workspace }} /> : <></>}
					</div>
					<div className="w-full lg:w-[420px] lg:flex-shrink-0">
						<PortalPreview
							workspace={workspace}
							settingsHash={previewSettingsHash}
							hasAnyPortalEnabled={hasAnyPortalEnabled}
						/>
					</div>
				</div>
			</SettingsCardInsetContext.Provider>
		</>
	)
}

interface ThemeFormProps {
	data: {
		workspace: Workspace
	}
}

function ThemeForm({ data }: ThemeFormProps) {
	const form = useZodForm({
		schema: ThemeFormSchema,
		defaultValues: {
			darkMode: data.workspace.darkMode,
			accentColor: data.workspace.accentColor,
			darkModeAccentColor: data.workspace.darkModeAccentColor,
			buttonTextColor: data.workspace.buttonTextColor,
			darkModeButtonTextColor: data.workspace.darkModeButtonTextColor,
		},
	})
	const zero = useZero()

	const watch = form.watch()

	const persist = async (
		partial: Partial<
			Pick<
				Workspace,
				| "darkMode"
				| "accentColor"
				| "darkModeAccentColor"
				| "buttonTextColor"
				| "darkModeButtonTextColor"
				| "portalBackgroundColor"
				| "portalDarkModeBackgroundColor"
			>
		>,
	) => {
		try {
			await mutateOk(
				zero.mutate(
					mutators.workspaces.update({
						id: data.workspace.id,
						...partial,
					}),
				),
			)
			analytics.track("update-theme-settings")
		} catch {
			toast.error({ title: "Could not save theme settings" })
		}
	}

	useThemeColorAutosave({
		colors: watch,
		isDirty: (field) => !!form.formState.dirtyFields[field],
		persist: (field, value) => void persist({ [field]: value }),
	})

	return (
		<SettingsContainer>
			<Form className="space-y-8" form={form} onSubmit={() => undefined}>
				<div className="flex flex-wrap items-center justify-between gap-4">
					<div>
						<p className="text-sm font-medium">Logo</p>
						<p className="text-xs text-secondary">
							Shown in the portal header, the widget and your emails. 256x256 px
							recommended.
						</p>
					</div>
					<WorkspaceLogoField
						workspaceId={data.workspace.id}
						logoUrl={data.workspace.logoUrl ?? null}
					/>
				</div>

				<hr className="border-gray-200 dark:border-gray-700/80" />

				<EmbedImageField
					imageUrl={data.workspace.portalEmbedImageUrl ?? null}
					onUploaded={async (fileUrl) => {
						await mutateOk(
							zero.mutate(
								mutators.workspaces.update({
									id: data.workspace.id,
									portalEmbedImageUrl: fileUrl,
								}),
							),
						)
					}}
					onCleared={async () => {
						await mutateOk(
							zero.mutate(
								mutators.workspaces.update({
									id: data.workspace.id,
									portalEmbedImageUrl: null,
								}),
							),
						)
						analytics.track("unset-embed-portal-image", {
							id: data.workspace.id,
						})
					}}
				/>

				<hr className="border-gray-200 dark:border-gray-700/80" />
				<DarkModeField
					value={watch.darkMode}
					onChange={(next) => {
						form.setValue("darkMode", next)
						void persist({ darkMode: next })
					}}
				/>
				<hr className="border-gray-200 dark:border-gray-700/80" />

				<ThemeColorFields
					colors={watch}
					onChange={(field, hex) =>
						form.setValue(field, hex, { shouldDirty: true })
					}
					onCommit={(field) => void persist({ [field]: watch[field] })}
					backgrounds={{
						portalBackgroundColor: data.workspace.portalBackgroundColor ?? null,
						portalDarkModeBackgroundColor:
							data.workspace.portalDarkModeBackgroundColor ?? null,
					}}
					onBackgroundSave={(field, value) => void persist({ [field]: value })}
				/>
			</Form>
		</SettingsContainer>
	)
}
